import type { Credential, Provider } from "@aiyo/shared";
import { CircleCheck, CircleXmark, Key, Pencil, Plus, TrashBin } from "@gravity-ui/icons";
import { Button, Card, Separator, Table, toast } from "@heroui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, FormDialog } from "../components/dialogs";
import { SelectInput, SwitchInput, TextInput } from "../components/fields";
import {
  type FormValues,
  initialValues,
  type JsonObjectSchema,
  SchemaForm,
  toConfig,
} from "../components/SchemaForm";
import {
  ChannelChip,
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  Page,
  PageHeader,
  StateChip,
} from "../components/ui";
import { validationIssues } from "../lib/api";
import { useFormat } from "../lib/format";
import { useLastDefined } from "../lib/hooks";
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useMe,
  useProviders,
  useTestCredential,
  useUpdateCredential,
} from "../lib/queries";

export function CredentialsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const credentials = useCredentials();
  const { data: me } = useMe();
  const test = useTestCredential();
  const remove = useDeleteCredential();
  const [editing, setEditing] = useState<Credential | "new" | null>(null);
  const [toDelete, setToDelete] = useState<Credential | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const canEdit = me?.role !== "viewer";

  const newButton = canEdit ? (
    <Button onPress={() => setEditing("new")}>
      <Plus />
      {t("credentials.new")}
    </Button>
  ) : null;

  const runTest = (c: Credential) => {
    setTestingId(c.id);
    test.mutate(c.id, {
      onSuccess: (r) =>
        r.ok
          ? toast.success(t("credentials.testOk"))
          : toast.danger(t("credentials.testFailed"), { description: r.error ?? "" }),
      onSettled: () => setTestingId(null),
    });
  };

  return (
    <Page>
      <PageHeader
        title={t("credentials.title")}
        description={t("credentials.subtitle")}
        actions={newButton}
      />
      {credentials.isPending ? (
        <LoadingBlock />
      ) : credentials.isError ? (
        <ErrorBlock error={credentials.error} onRetry={() => credentials.refetch()} />
      ) : credentials.data.length === 0 ? (
        <Card>
          <EmptyBlock
            icon={Key}
            title={t("credentials.empty")}
            hint={t("credentials.emptyHint")}
            action={newButton}
          />
        </Card>
      ) : (
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label={t("credentials.title")} className="min-w-[900px]">
              <Table.Header>
                <Table.Column isRowHeader>{t("common.name")}</Table.Column>
                <Table.Column>{t("credentials.provider")}</Table.Column>
                <Table.Column>{t("common.status")}</Table.Column>
                <Table.Column>{t("credentials.health")}</Table.Column>
                <Table.Column>{t("credentials.profilesCount")}</Table.Column>
                <Table.Column>{t("credentials.appsCount")}</Table.Column>
                <Table.Column className="text-end">{t("common.actions")}</Table.Column>
              </Table.Header>
              <Table.Body>
                {credentials.data.map((c) => (
                  <Table.Row key={c.id} id={c.id}>
                    <Table.Cell>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{c.name}</span>
                        <span className="max-w-[280px] truncate text-xs text-muted">
                          {Object.entries(c.config_public)
                            .slice(0, 3)
                            .map(([k, v]) => `${k}: ${String(v)}`)
                            .join(" · ")}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <ChannelChip channel={c.provider.channel} />
                        <span className="text-sm">{c.provider.name}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <StateChip ok={c.status === "active"} label={t(`status.credential.${c.status}`)} />
                    </Table.Cell>
                    <Table.Cell>
                      {c.health.last_checked_at ? (
                        <span
                          className="flex items-center gap-1.5 text-sm"
                          title={c.health.last_error ?? undefined}
                        >
                          {c.health.last_error ? (
                            <CircleXmark className="size-4 text-danger" />
                          ) : (
                            <CircleCheck className="size-4 text-success" />
                          )}
                          {fmt.relative(c.health.last_checked_at)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted">{t("credentials.notChecked")}</span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="tabular-nums">{c.used_by.profiles}</Table.Cell>
                    <Table.Cell className="tabular-nums">{c.used_by.apps}</Table.Cell>
                    <Table.Cell className="text-end">
                      {canEdit ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            isPending={testingId === c.id}
                            onPress={() => runTest(c)}
                          >
                            {t("credentials.test")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isIconOnly
                            aria-label={t("common.edit")}
                            onPress={() => setEditing(c)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isIconOnly
                            aria-label={t("common.delete")}
                            isDisabled={c.used_by.profiles > 0}
                            onPress={() => setToDelete(c)}
                          >
                            <TrashBin />
                          </Button>
                        </div>
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}

      <CredentialDialog target={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        isOpen={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t("credentials.deleteTitle")}
        body={t("credentials.deleteBody")}
        confirmLabel={t("common.delete")}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={() => toDelete && remove.mutate(toDelete.id, { onSuccess: () => setToDelete(null) })}
      />
    </Page>
  );
}

function CredentialDialog({ target, onClose }: { target: Credential | "new" | null; onClose: () => void }) {
  const { t } = useTranslation();
  const providers = useProviders();
  const create = useCreateCredential();
  const update = useUpdateCredential();
  const shown = useLastDefined(target);
  const existing = shown && shown !== "new" ? shown : null;

  const [providerId, setProviderId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [values, setValues] = useState<FormValues>({});

  const provider: Provider | undefined = useMemo(
    () => providers.data?.find((p) => p.id === (existing?.provider.id ?? providerId)),
    [providers.data, existing, providerId],
  );
  const schema = (provider?.config_schema ?? {}) as JsonObjectSchema;

  // biome-ignore lint/correctness/useExhaustiveDependencies: the form resets only when the dialog target changes.
  useEffect(() => {
    if (!target) return;
    create.reset();
    update.reset();
    setProviderId(existing?.provider.id ?? null);
    setName(existing?.name ?? "");
    setActive(existing ? existing.status === "active" : true);
  }, [target]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: config fields reset when the provider or target changes.
  useEffect(() => {
    if (provider) setValues(initialValues(schema, existing?.config_public));
  }, [provider?.id, target]);

  const mutation = existing ? update : create;
  const fieldErrors = Object.fromEntries(validationIssues(mutation.error).map((i) => [i.path, i.message]));

  const submit = () => {
    if (!provider) return;
    const done = {
      onSuccess: () => {
        toast.success(t("credentials.saved"));
        onClose();
      },
    };
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          name,
          status: active ? "active" : "disabled",
          config: toConfig(schema, values, "edit"),
        },
        done,
      );
    } else {
      create.mutate({ provider_id: provider.id, name, config: toConfig(schema, values, "create") }, done);
    }
  };

  return (
    <FormDialog
      isOpen={target !== null}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={existing ? t("credentials.editTitle") : t("credentials.createTitle")}
      submitLabel={t("common.save")}
      isPending={mutation.isPending}
      isSubmitDisabled={!provider}
      error={mutation.error}
      onSubmit={submit}
    >
      <SelectInput
        label={t("credentials.provider")}
        placeholder={t("credentials.chooseProvider")}
        value={existing?.provider.id ?? providerId}
        onChange={setProviderId}
        isDisabled={Boolean(existing)}
        isRequired
        options={(providers.data ?? []).map((p) => ({
          id: p.id,
          label: p.name,
          hint: `${t(`channels.${p.channel}`)} · ${p.code}${p.status !== "active" ? ` · ${t(`status.provider.${p.status}`)}` : ""}`,
        }))}
      />
      <TextInput
        name="credential-name"
        label={t("credentials.name")}
        placeholder={t("credentials.namePlaceholder")}
        value={name}
        onChange={setName}
        isRequired
      />
      {existing ? (
        <SwitchInput
          label={t("common.active")}
          description={t("credentials.disableHint")}
          isSelected={active}
          onChange={setActive}
        />
      ) : null}
      {provider ? (
        <>
          <Separator />
          <div className="flex flex-col gap-5">
            <div className="text-sm font-semibold">{t("credentials.config")}</div>
            <SchemaForm
              schema={schema}
              values={values}
              onChange={setValues}
              secretFieldsSet={existing?.secret_fields_set ?? []}
              errors={fieldErrors}
            />
          </div>
        </>
      ) : null}
    </FormDialog>
  );
}
